import {
  signUp,
  confirmSignUp,
  signIn,
  signOut,
  resendSignUpCode,
  updateUserAttribute,
  type UpdateUserAttributeOutput,
  confirmUserAttribute,
  updatePassword,
  type UserAttributeKey,
  confirmResetPassword,
  resetPassword,
} from 'aws-amplify/auth';
import { SignUp } from '@/types/schemas/sign-up';
import { UpdatePassword } from '@/types/schemas/update-password';
import { Result } from '@/types/result-errors';
import { ConfirmSignup } from '@/types/schemas/confirm-sign-up';
import { SignIn } from '@/types/schemas/sign-in';

// Simple error types for client-side auth
type AuthError = Error;
type UpdateUserAttributeError = Error;

export async function handleSignUp(
  data: SignUp,
): Promise<Result<void, AuthError>> {
  try {
    await signUp({
      username: data.email,
      password: data.password,
      options: {
        userAttributes: {
          email: data.email,
          ['custom:roleToAssign']: 'ADMIN',
        },
        autoSignIn: true,
      },
    });
    return { value: undefined, error: null };
  } catch (error) {
    console.error(error);
    return { value: null, error: error as AuthError };
  }
}

export async function handleSendEmailVerificationCode(
  email: string,
): Promise<Result<void, AuthError>> {
  try {
    await resendSignUpCode({ username: email });
    return { value: undefined, error: null };
  } catch (error) {
    console.log(error);
    return { value: null, error: error as AuthError };
  }
}

export async function handleConfirmSignUp(
  data: ConfirmSignup & { email: string },
): Promise<Result<void, AuthError>> {
  try {
    await confirmSignUp({
      username: data.email,
      confirmationCode: data.code,
    });
    return { value: undefined, error: null };
  } catch (error) {
    console.log(error);
    return { value: null, error: error as AuthError };
  }
}

export type SignInResult = Result<
  { nextStep?: string } | void,
  AuthError
>;

export async function handleSignIn(
  data: SignIn,
): Promise<SignInResult> {
  try {
    const { nextStep } = await signIn({
      username: data.email,
      password: data.password,
    });

    if (nextStep.signInStep === 'CONFIRM_SIGN_UP') {
      await resendSignUpCode({ username: data.email });
      return {
        value: null,
        error: Object.assign(new Error('CONFIRM_SIGN_UP'), {
          name: 'ConfirmSignUp',
        }),
      };
    }

    if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
      await resendSignUpCode({ username: data.email });
      return {
        value: null,
        error: Object.assign(new Error('NEW_PASSWORD_REQUIRED'), {
          name: 'NewPasswordRequired',
        }),
      };
    }

    return { value: undefined, error: null };
  } catch (error) {
    console.log(error);
    return { value: null, error: error as AuthError };
  }
}

export async function handleSignOut(): Promise<Result<void, AuthError>> {
  try {
    await signOut();
    return { value: undefined, error: null };
  } catch (error) {
    console.log(error);
    return { value: null, error: error as AuthError };
  }
}

export async function handleUpdateUserAttribute(
  attributeKey: UserAttributeKey,
  attributeValue: string,
): Promise<Result<string, UpdateUserAttributeError>> {
  try {
    const output = await updateUserAttribute({
      userAttribute: {
        attributeKey: attributeKey,
        value: attributeValue,
      },
    });
    return { value: handleUpdateUserAttributeNextSteps(output), error: null };
  } catch (error) {
    console.log(error);
    return { value: null, error: error as UpdateUserAttributeError };
  }
}

function handleUpdateUserAttributeNextSteps(
  output: UpdateUserAttributeOutput,
): string {
  const { nextStep } = output;
  const codeDeliveryDetails = nextStep.codeDeliveryDetails;

  switch (nextStep.updateAttributeStep) {
    case 'CONFIRM_ATTRIBUTE_WITH_CODE':
      return `Confirmation code was sent to ${codeDeliveryDetails?.deliveryMedium}.`;
    case 'DONE':
      return 'success';
  }
}

export async function handleUpdatePassword(
  data: UpdatePassword,
): Promise<Result<void, UpdateUserAttributeError>> {
  try {
    await updatePassword({
      oldPassword: data.currentPassword,
      newPassword: data.newPassword,
    });
    return { value: undefined, error: null };
  } catch (error) {
    console.log(error);
    if (error instanceof Error && error.name === 'NotAuthorizedException') {
      return { value: null, error: new Error('Incorrect current password') };
    }
    return { value: null, error: error as UpdateUserAttributeError };
  }
}

export async function handleConfirmUserAttribute(
  pin: string,
): Promise<Result<string, UpdateUserAttributeError>> {
  try {
    await confirmUserAttribute({
      userAttributeKey: 'email',
      confirmationCode: pin,
    });
    return { value: 'Attribute changed successfully', error: null };
  } catch (error) {
    console.log(error);
    return { value: null, error: error as UpdateUserAttributeError };
  }
}

export async function handleForgotPassword(
  username: string,
): Promise<Result<string, AuthError>> {
  try {
    await resetPassword({ username });
    return {
      value: 'One-time-password code has been sent. Check your email inbox.',
      error: null,
    };
  } catch (error) {
    console.log(error);
    return { value: null, error: error as AuthError };
  }
}

export async function handleConfirmForgotPassword(
  username: string,
  confirmationCode: string,
  newPassword: string,
): Promise<Result<string, AuthError>> {
  try {
    await confirmResetPassword({
      username,
      confirmationCode,
      newPassword,
      options: { clientMetadata: { type: 'password-recovery' } },
    });
    return { value: 'Password has been reset successfully.', error: null };
  } catch (error) {
    console.log(error);
    return { value: null, error: error as AuthError };
  }
}
